import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Box,
  Dialog,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import DeleteEntityDialog from "components/DeleteEntityDialog";
import Loader from "components/Loader";
import RedirectBackButton from "components/RedirectBackButton";
import SnackbarMessage from "components/SnackbarMessage";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import useTitle from "hooks/useTitle";
import useTrashablePromises from "hooks/useTrashablePromises";
import { useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { Navigate, useNavigate, useParams, useLocation } from "react-router-dom";
import { MediaItemSource, ResourceV2 } from "services/KnowfullyAdminClient";
import { SnackbarMessageProps } from "types/common-types";
import LinkFacultyPortalUserDialog from "./LinkFacultyPortalUserDialog";
import ResourceDetailsTopButtons from "./ResourceDetailsTopButtons";
import ResourceUpdateForm from "./ResourceUpdateForm";
import UnlinkFacultyPortalUserDialog from "./UnlinkFacultyPortalUserDialog";

type HistoryLocationStateProps = {
  from: string | undefined;
};

const styles = {
  multiline: {
    whiteSpace: "pre-line",
  },
};

const ResourceDetails = () => {
  const { formatMessage: t } = useIntl();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { registerPromise } = useTrashablePromises();
  const { id } = useParams<{ id: string }>();
  const {
    resourcesUrl,
    resourceSkusUrl,
    resourceService,
    mediaService,
    userDetailsUrl,
  } = useGlobalContext();
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);
  const [editOpen, editOpenActions] = useBoolean(false);
  const [deleteOpen, deleteOpenActions] = useBoolean(false);
  const [linkUserToFaculty, setLinkUserToFaculty] = useState<ResourceV2>();
  const [
    unlinkUserFromFaculty,
    setUnlinkUserFromFaculty,
  ] = useState<ResourceV2>();

  const { isLoading, isError, data } = useQuery(
    ["resource", id],
    () => resourceService.getResource(id!),
    {
      enabled: !!id,
    }
  );

  const { data: picture, isLoading: isLoadingPicture } = useQuery(
    ["media-item", data?.data?.mediaLibraryItemId],
    () =>
      mediaService.getItem(
        data?.data?.mediaLibraryItemId!,
        MediaItemSource.MediaLibrary
      ),
    {
      enabled: !!data?.data?.mediaLibraryItemId,
    }
  );

  const resource = data?.data;
  useTitle(resource?.name || t({ id: "facultyLabel" }));

  if (!id) return <Navigate to={resourcesUrl()} replace />;
  if (isLoading || isLoadingPicture) return <Loader />;
  if (isError) {
    return (
      <Box mt={4}>
        <Alert severity="error">
          <T id="fetchError" />
        </Alert>
      </Box>
    );
  }

  const onResourceUpdated = () => {
    editOpenActions.setFalse();
    setLinkUserToFaculty(undefined);
    setUnlinkUserFromFaculty(undefined);

    queryClient.invalidateQueries(["resource", id]);
    queryClient.invalidateQueries(["resources"]);
    setSnackbarMessage(new SnackbarMessageProps(t({ id: "facultyUpdated" })));
  };

  const handleDelete = async () => {
    await registerPromise(resourceService.deleteResource(resource!.id!));
    queryClient.invalidateQueries("resources");
    navigate(resourcesUrl());
  };

  const imageRow = picture?.data?.fileUrl ? (
    <Box
      component="img"
      sx={{ height: "auto", width: "150px" }}
      alt="facultyImage"
      src={picture.data.fileUrl}
    />
  ) : null;

  const bioRow = resource?.bio ? (
    <div dangerouslySetInnerHTML={{ __html: resource.bio }}></div>
  ) : null;
  const rows = [
    {
      title: t({ id: "organization" }),
      component: resource?.organizationName && (
        <span>{resource.organizationName}</span>
      ),
    },
    {
      title: t({ id: "profilePictureLabel" }),
      component: imageRow,
    },
    {
      title: t({ id: "prefixLabel" }),
      component: resource?.prefix && <span>{resource.prefix}</span>,
    },
    {
      title: t({ id: "nameLabel" }),
      component: <span>{resource?.name}</span>,
    },
    {
      title: t({ id: "bioLabel" }),
      component: bioRow,
    },
    {
      title: t({ id: "descriptionLabel" }),
      component: resource?.summary && (
        <Box component="span" sx={styles.multiline}>
          {resource.summary}
        </Box>
      ),
    },
    {
      title: t({ id: "koneUser" }),
      component: resource?.organizationUserId && (
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          {resource.organizationUserId}
          <OpenInNewIcon
            fontSize="small"
            sx={{ cursor: "pointer" }}
            onClick={() =>
              navigate(userDetailsUrl(resource.organizationUserId))
            }
          />
        </Box>
      ),
    },
    {
      title: t({ id: "crmEmailLabel" }),
      component: resource?.crmEmail && <span>{resource.crmEmail}</span>,
    },
    {
      title: t({ id: "crmIdLabel" }),
      component: resource?.crmId && <span>{resource.crmId}</span>,
    },
  ].filter((r) => !!r.component);

  return (
    <>
      <Box my={2}>
        <RedirectBackButton
          redirectRoute={(location.state as HistoryLocationStateProps)?.from ?? resourcesUrl()}
          labelKey="backLabel"
        />
      </Box>
      <h1>{resource?.name}</h1>

      <ResourceDetailsTopButtons
        resource={resource!}
        onAddClick={editOpenActions.setTrue}
        onSkusClick={() => navigate(resourceSkusUrl(id))}
        onLinkUserClick={() => setLinkUserToFaculty(resource)}
        onUnlinkUserClick={() => setUnlinkUserFromFaculty(resource)}
        onDeleteClick={deleteOpenActions.setTrue}
      />

      <Dialog open={editOpen} disableEnforceFocus maxWidth="md" fullWidth>
        <ResourceUpdateForm
          resource={resource!}
          onResourceUpdated={onResourceUpdated}
          onCancel={editOpenActions.setFalse}
        />
      </Dialog>

      <Box my={3}>
        <TableContainer>
          <Table>
            <TableBody>
              {rows.map(({ title, component }, i) => (
                <TableRow key={i}>
                  <TableCell component="th" scope="row">
                    <b>{title}</b>
                  </TableCell>
                  <TableCell>{component}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <DeleteEntityDialog
        dialogTitleKey="resourceDeleteTitle"
        contentKey="resourceDeleteInfo"
        onCancel={deleteOpenActions.setFalse}
        handleDelete={handleDelete}
        open={deleteOpen}
      />

      <LinkFacultyPortalUserDialog
        resource={linkUserToFaculty}
        onClose={() => setLinkUserToFaculty(undefined)}
        onSuccess={onResourceUpdated}
      />

      <UnlinkFacultyPortalUserDialog
        resource={unlinkUserFromFaculty}
        onClose={() => setUnlinkUserFromFaculty(undefined)}
        onSuccess={onResourceUpdated}
      />

      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </>
  );
};

export default ResourceDetails;
